import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer, CartesianGrid
} from 'recharts';
import dayjs from 'dayjs';

const ChartWithTimeframe = ({ fullData }) => {
  const timeframes = {
    '1W': 7,
    '1M': 30,
    '3M': 90,
    '6M': 180,
    '1Y': 365,
    'ALL': null
  };
  const [timeframe, setTimeframe] = useState('3M');

  const filterData = () => {
    let filtered =
      timeframes[timeframe] === null
        ? fullData
        : fullData.filter(d =>
            dayjs(d.date).isAfter(dayjs().subtract(timeframes[timeframe], 'day'))
          );
    return filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const displayedData = filterData();

  const formatMillions = (value) => {
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
    return value.toFixed(2);
  };

  return (
    <div style={{
      width: "100%",
      backgroundColor: "#111",
      borderRadius: 12,
      padding: 10,
      color: "#fff",
      gap: 10,
      alignItems: "center",
      position: "relative",
      overflow: "hidden"
    }}>
      {/* Timeframe Buttons */}
      <div className="d-flex justify-content-end flex-wrap gap-2">
        <div className="d-flex" style={{ marginBottom: 10 }}>
          <div className="rTabs">
            {Object.keys(timeframes).map(tf => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                style={{
                  marginRight: 8,
                  padding: '6px 12px',
                  background:
                    timeframe === tf
                      ? 'linear-gradient(94.35deg, #F8F192 0.05%, #E3D578 26.62%, #B18D38 56.99%, #9D721F 98.73%)'
                      : '#2c2c2c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ marginLeft: '-40px' }}>
        <ResponsiveContainer width="100%" height={500}>

          <AreaChart data={displayedData} margin={{ left: 0, right: 0 }}>
            <defs>
              <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9D721F" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#121212" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorLiquidity" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F8F192" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#121212" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorMCap" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E3D578" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#121212" stopOpacity={0} />
              </linearGradient>
            </defs>
            
              {/* Add Cartesian Grid */}
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fill: '#ccc', fontSize: 12 }} tickFormatter={date => dayjs(date).format('DD MMM, YYYY')}/>
            <YAxis
              yAxisId="price"
              label={{ value: 'Price', angle: -90, position: 'insideLeft', fill: '#ccc', offset: 10 }}
              tick={{ fill: '#ccc', fontSize: 12 }}
              orientation="left"
              offset={0}
            />
            <YAxis
              yAxisId="volume"
              tickFormatter={formatMillions}
              orientation="left"
              offset={0}
              axisLine={false}  
              tickLine={false} 
              tick={false} 
            />
            <YAxis
              yAxisId="marketcap"
              label={{ value: 'Market Cap', angle: -90, position: 'insideRight', fill: '#ccc', offset: 10 }}
              tickFormatter={formatMillions}
              tick={{ fill: '#ccc', fontSize: 12 }}
              orientation="right"
              offset={0}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#333', border: 'none' }}
              labelStyle={{ color: '#fff' }}
              formatter={(value, name) => {
                const labelMap = {
                  MarketCap: 'Market Cap',
                  Price: 'Price',
                  Volume: 'Volume'
                };

                let formattedValue;
                if (name === 'Price') {
                  formattedValue = '$' + parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 8 })
                } else {
                  formattedValue = '$' + parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                }
                return [formattedValue, labelMap[name] || name];
              }}
              labelFormatter={label => `Date: ${dayjs(label).format('DD MMM, YYYY')}`}
            />

            <Area type="monotone" dataKey="Price" stroke="#F8F192" fill="url(#colorLiquidity)" fillOpacity={1}  yAxisId="price" />
            <Area type="monotones" dataKey="Volume" stroke="#9D721F" fill="url(#colorVolume)" fillOpacity={1}  yAxisId="volume" />
            <Area type="monotone" dataKey="MarketCap" stroke="#E3D578" fill="url(#colorMCap)" fillOpacity={1}  yAxisId="marketcap" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ChartWithTimeframe;
